import { useEffect, useState } from 'react';
import { api, money, quantity as qtyText } from '../api.js';
import NumBox from './NumBox.jsx';

/**
 * Поправка на партидите по една стока.
 *
 * Случаят: доставката е въведена в грешна партида и оттам нататък
 * себестойността на всяка продажба тръгва от чужда цена. Тук стоката се
 * пренарежда по партиди.
 *
 * Сборът трябва да излезе колкото е наличността. Не излезе ли, има два
 * изхода и човекът избира: или поправя числата, защото се е объркал при
 * въвеждането, или казва „толкова са" — и тогава разликата се записва
 * като ревизия, с ред по всяка пипната партида.
 *
 * Второто не е скрито разместване: остава документ с номер, който казва
 * кой и кога го е решил.
 *
 * Празните партиди се показват по желание. Обикновено са десетки и само
 * пречат, но точно в тях трябва да влезе количество, попаднало на грешно
 * място.
 */
export default function BatchEditor({ productId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [prazni, setPrazni] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [kato, setKato] = useState(null);      // потвърждение за ревизията
  const [note, setNote] = useState('');

  useEffect(() => {
    api.get(`/api/products/${productId}/batches`)
      .then((d) => {
        setData(d);
        const f = {};
        for (const b of d.batches) {
          f[b.id] = {
            quantity: String(Number(b.quantity)),
            price: b.purchase_price === null ? '' : String(Number(b.purchase_price)),
          };
        }
        setForm(f);
      })
      .catch((err) => setError(err.message));
  }, [productId]);

  if (error && !data) return <div className="error">{error}</div>;
  if (!data) return <div className="note">…</div>;

  const set = (id, part) => {
    setForm((f) => ({ ...f, [id]: { ...f[id], ...part } }));
    setError(null);
  };

  // Сборът се мери по складове: партида от Варна не покрива липса другаде.
  const poSklad = new Map();
  for (const b of data.batches) {
    const k = b.warehouse_id;
    const q = Number(form[b.id]?.quantity || 0);
    poSklad.set(k, (poSklad.get(k) ?? 0) + q);
  }
  const skladove = data.stock.map((s) => {
    const sbor = Math.round((poSklad.get(s.warehouse_id) ?? 0) * 10000) / 10000;
    const sklad = Math.round(Number(s.quantity) * 10000) / 10000;
    return { ...s, sbor, sklad, razlika: Math.round((sbor - sklad) * 10000) / 10000 };
  });
  // Склад, в който има партиди, но наличност няма — и той трябва да излезе.
  for (const [wid, sbor] of poSklad) {
    if (!skladove.some((s) => s.warehouse_id === wid)) {
      const ime = data.batches.find((b) => b.warehouse_id === wid)?.warehouse_name ?? '';
      skladove.push({ warehouse_id: wid, warehouse_name: ime, sbor, sklad: 0, razlika: sbor });
    }
  }

  const vrazva = skladove.every((s) => Math.abs(s.razlika) < 0.0005);
  const promenen = data.batches.some((b) => {
    const f = form[b.id] ?? {};
    return Number(f.quantity || 0) !== Number(b.quantity)
      || Number(f.price || 0) !== Number(b.purchase_price ?? 0);
  });

  async function save(asRevision = false) {
    setBusy(true);
    setError(null);
    try {
      const rows = data.batches.map((b) => ({
        id: b.id,
        quantity: Number(form[b.id]?.quantity || 0),
        purchasePrice: form[b.id]?.price === '' ? null : Number(form[b.id]?.price),
      }));
      const res = await api.put(`/api/products/${productId}/batches`, {
        rows,
        ...(asRevision ? { asRevision: true, note: note.trim() || null } : {}),
      });
      onSaved?.(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const vidimi = data.batches.filter((b) => prazni || Number(b.quantity) !== 0
    || Number(form[b.id]?.quantity || 0) !== 0);
  const skriti = data.batches.length - vidimi.length;

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        Партиди на „{data.product.name}"
        <span className="spacer" />
        <button className="link" onClick={onClose}>затвори</button>
      </div>
      <div className="card-body">
        <p className="hint" style={{ marginTop: 0 }}>
          Тук стоката се пренарежда по партиди. Общото количество по склада не се
          променя оттук — за него е ревизията.
        </p>

        {skladove.map((s) => (
          <div key={s.warehouse_id} style={{ marginBottom: 6 }}>
            <b>{s.warehouse_name}</b>
            {': по склад '}{qtyText(s.sklad)}{', по партиди '}{qtyText(s.sbor)}
            {Math.abs(s.razlika) > 0.0005
              ? (
                <span className="badge danger" style={{ marginLeft: 8 }}>
                  разлика {s.razlika > 0 ? '+' : ''}{qtyText(s.razlika)}
                </span>
              )
              : <span className="badge ok" style={{ marginLeft: 8 }}>връзва се</span>}
          </div>
        ))}

        {error && <div className="error" style={{ marginTop: 10 }}>{error}</div>}

        <table className="inner" style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>Партида</th>
              <th>Склад</th>
              <th className="num">Количество</th>
              <th className="num">Доставна цена</th>
              <th className="num">Стойност</th>
            </tr>
          </thead>
          <tbody>
            {vidimi.map((b) => {
              const f = form[b.id] ?? {};
              const smenen = Number(f.quantity || 0) !== Number(b.quantity)
                || Number(f.price || 0) !== Number(b.purchase_price ?? 0);
              return (
                <tr key={b.id} className={smenen ? 'tree-found' : undefined}>
                  <td>{b.batch_no}</td>
                  <td className="muted">{b.warehouse_name}</td>
                  <td className="num">
                    <NumBox className="qty num" value={f.quantity ?? ''}
                      onChange={(v) => set(b.id, { quantity: v })} />
                  </td>
                  <td className="num">
                    <NumBox className="price num" placeholder="няма" value={f.price ?? ''}
                      onChange={(v) => set(b.id, { price: v })} />
                  </td>
                  <td className="num muted">
                    {money(Number(f.quantity || 0) * Number(f.price || 0))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {skriti > 0 && !prazni && (
          <button className="link" style={{ marginTop: 8 }} onClick={() => setPrazni(true)}>
            покажи и {skriti} празни партиди
          </button>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
          <button className="primary" disabled={busy || !promenen || !vrazva}
            onClick={() => save(false)}>
            Запиши
          </button>
          {/* Изходът, когато разликата е истинска: наличността тръгва
              след партидите, но остава документ за това. */}
          {!vrazva && promenen && !kato && (
            <button disabled={busy} onClick={() => setKato('revizia')}>
              Толкова са — запиши ревизия
            </button>
          )}
          <button disabled={busy} onClick={onClose}>Откажи</button>
          {!vrazva && !kato && (
            <span className="hint">
              Сборът не отговаря на наличността. Или поправи числата, или запиши ревизия.
            </span>
          )}
        </div>

        {kato === 'revizia' && (
          <div className="note" style={{ marginTop: 12 }}>
            <p style={{ marginTop: 0 }}>
              Наличността ще се промени, за да съвпадне с партидите:
            </p>
            <ul style={{ margin: '6px 0 10px' }}>
              {skladove.filter((x) => Math.abs(x.razlika) > 0.0005).map((x) => (
                <li key={x.warehouse_id}>
                  <b>{x.warehouse_name}</b>: {qtyText(x.sklad)} → {qtyText(x.sbor)}
                  {' ('}{x.razlika > 0 ? '+' : ''}{qtyText(x.razlika)}{')'}
                </li>
              ))}
            </ul>
            <label className="field" style={{ maxWidth: 420 }}>
              Основание
              <input value={note} placeholder="какво е установено при броенето"
                onChange={(e) => setNote(e.target.value)} />
            </label>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button className="primary" disabled={busy} onClick={() => save(true)}>
                Запиши ревизията
              </button>
              <button disabled={busy} onClick={() => setKato(null)}>Върни се</button>
            </div>
            <span className="hint">
              Ще се издаде документ „ревизия" с ред по всяка пипната партида.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
